import React, { useContext, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Modal, Row, Button } from 'react-bootstrap';
import { RootStoreContext } from 'src/stores/RootStore';
import { PopupPriority } from 'src/stores/PopupStore';
import { StepName } from 'src/config/Wizard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { EVENTS_AD_COOLDOWN_KEY, markShown } from 'src/util/displayCooldown';
import './EventPhotosView.css';
import { PromotionItem } from 'src/stores/EventPhotoStore';

const POPUP_ID = 'event-photos-popup';

const EventPhotosPopup = observer(() => {
  const { eventPhotoStore, wizardStore: { setStep }, registrationStore, popupStore } = useContext(RootStoreContext);

  const [showModal, setShowModal] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [manualNavigation, setManualNavigation] = useState(false);
  const [promotions] = useState<PromotionItem[]>(() => [...eventPhotoStore.promotionItems]);

  const hasPhotos = promotions.length > 0;
  const activePromotion = promotions[activeIndex];

  // Auto-advance carousel every 4 seconds
  useEffect(() => {
    if (!hasPhotos || promotions.length <= 1 || manualNavigation) return;

    const timer = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % promotions.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [hasPhotos, manualNavigation, promotions.length]);

  const goToPrevious = () => {
    setManualNavigation(true);
    setActiveIndex((prevIndex) => (prevIndex - 1 + promotions.length) % promotions.length);
  };

  const goToNext = () => {
    setManualNavigation(true);
    setActiveIndex((prevIndex) => (prevIndex + 1) % promotions.length);
  };

  const handleClose = () => {
    setShowModal(false);
    registrationStore.activeCategory = 0;
    registrationStore.selectedEventId = null;
    setStep(StepName.REGISTRATION_SELECT_TRAINING);
  };

  const handleCategorySelect = () => {
    const promotion = promotions[activeIndex];
    if (promotion?.type === 'event') {
      registrationStore.activeCategory = 1;
      registrationStore.selectedEventId = promotion.eventId;
      setShowModal(false);
      setStep(StepName.REGISTRATION_SELECT_TRAINING);
    }
  };

  const handleCampaignSelect = () => {
    const promotion = promotions[activeIndex];
    if (promotion?.type === 'ad_campaign') {
      eventPhotoStore.selectCampaign(promotion.id);
      setShowModal(false);
      setStep(StepName.WHATS_NEW);
    }
  };

  useEffect(() => {
    if (hasPhotos && showModal) {
      popupStore.register(POPUP_ID, PopupPriority.EVENTS);
      return () => popupStore.unregister(POPUP_ID);
    }
  }, [hasPhotos, showModal, popupStore]);

  // Record the events ad as shown once it actually becomes visible, so it
  // respects the 60-minute cooldown on the next app open / refresh.
  const userId = registrationStore.user?.id;
  const isDisplayed = hasPhotos && showModal && popupStore.isActive(POPUP_ID);
  useEffect(() => {
    if (isDisplayed && userId) {
      markShown(EVENTS_AD_COOLDOWN_KEY, userId);
    }
  }, [isDisplayed, userId]);

  if (!hasPhotos || !showModal) return null;
  // Stay registered while a higher-priority popup (e.g. the streak reward)
  // is active; reappear once it unregisters, rather than closing permanently.
  if (popupStore.isYielding(POPUP_ID)) return null;

  return (
    <Container>
      <Row>
        <Modal
          show={showModal}
          onHide={handleClose}
          backdrop
          keyboard
          centered
          dialogClassName="event-photos-dialog border-0"
          contentClassName="bg-white event-photos-modal"
        >
            <Modal.Header closeButton className="border-0" />
            <Modal.Body className="p-0" style={{ cursor: 'default' }}>
              <div className="promotion-overview promotion-overview--content-sized">
                <div className="custom-carousel-container">
                  <div className="custom-carousel-wrapper">
                    {promotions.map((promotion, index) => (
                      <div
                        key={promotion.key}
                        className={`custom-carousel-item ${index === activeIndex ? 'active' : ''}`}
                      >
                        <div className="promotion-image-frame">
                          <img
                            className="promotion-image"
                            src={promotion.type === 'ad_campaign' ? promotion.imageUrl : promotion.photoUrl}
                            alt={promotion.type === 'ad_campaign' ? promotion.title : `Event ${promotion.eventId}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {promotions.length > 1 && (
                    <>
                      <button
                        className="custom-carousel-control custom-carousel-control-prev"
                        onClick={goToPrevious}
                        aria-label="Previous"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <button
                        className="custom-carousel-control custom-carousel-control-next"
                        onClick={goToNext}
                        aria-label="Next"
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </>
                  )}
                </div>
                <div className="event-photos-view__cta p-3">
                  <Button
                    variant="primary"
                    onClick={activePromotion?.type === 'ad_campaign' ? handleCampaignSelect : handleCategorySelect}
                    className="event-photos-view__cta-button w-100 py-2"
                    style={{
                      backgroundColor: '#013220',
                      borderColor: '#013220',
                      color: 'white',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {activePromotion?.type === 'ad_campaign' ? 'View Info' : 'View Event'}
                  </Button>
                </div>
              </div>
            </Modal.Body>
        </Modal>
      </Row>
    </Container>
  );
});

export default EventPhotosPopup;
